import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { getAudioComposition } from "../../../api";
import { useAudioEditor } from "../../../context/AudioEditorContext";
import { AudioComposition, AudioCompositionClip, AudioEditorTrackType } from "../../../types/types";
import useAudioEditorNotifications from "./hooks/useAudioEditorNotifications";
import { rescaleCompositionClips, toEditorSession, voiceStartOf } from "./utils";
import { ImportedAudioEditorTrackType } from "../../../context/AudioEditorContext";

interface AudioEditorBootstrapProps {
    /** Audio the editor was opened on, from Projects or Drafts. */
    audioAIId?: string | null;
}

const clipToTrack = (clip: AudioCompositionClip): AudioEditorTrackType => ({
    id: uuidv4(),
    name: clip.name,
    url: clip.url,
    description: "",
    role: clip.role,
    sourceKind: clip.sourceKind,
    sourceId: clip.sourceId,
    startTime: clip.startTime,
    cueIn: clip.cueIn,
    cueOut: clip.cueOut,
    gain: clip.gain,
    muted: clip.muted,
    fadeIn: clip.fadeIn,
    fadeOut: clip.fadeOut,
    // Who is heard, as recorded when the mix was saved. Dropping it here would lose the
    // voice columns of the row on the next save, since they are derived from the timeline.
    sourceVoice: clip.voice,
    // What is said in this clip, as validated last time. Restoring it is what makes the
    // transcript step show the same thing after a save as before it.
    lines: clip.lines,
});

/**
 * Media strip entries for the clips that were laid down, deduplicated by url, so the
 * user can drop any of them back onto the timeline without searching the library again.
 * Built here rather than through `addToImportedList` so each entry keeps its real role
 * instead of having it guessed back from the file name.
 */
const buildImportedList = (
    composition: AudioComposition | null,
    voice: { id: string; name: string; url: string } | null,
): ImportedAudioEditorTrackType[] => {
    const byUrl = new Map<string, ImportedAudioEditorTrackType>();

    const add = (entry: Omit<ImportedAudioEditorTrackType, "editorUID">) => {
        if (!entry.url || byUrl.has(entry.url)) return;
        byUrl.set(entry.url, { ...entry, editorUID: uuidv4() });
    };

    if (voice) {
        add({
            id: voice.id,
            name: voice.name,
            url: voice.url,
            role: "voice",
            sourceKind: "audioAI",
            sourceId: voice.id,
        });
    }
    (composition?.tracks || []).forEach((clip) =>
        add({
            id: clip.sourceId || clip.clipId,
            name: clip.name,
            url: clip.url,
            role: clip.role,
            sourceKind: clip.sourceKind,
            sourceId: clip.sourceId,
        }),
    );

    return [...byUrl.values()];
};

/**
 * Preloads the editor when it is opened on an existing audio.
 *
 * Loads that audio's voice, then the sound design resolved for it: its own if it is a
 * mix, otherwise the one of the content it was translated from. Resolution happens here,
 * at open time, rather than being precomputed across every language.
 *
 * Renders nothing; it only drives the editor context.
 */
const AudioEditorBootstrap: React.FC<AudioEditorBootstrapProps> = ({ audioAIId }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { notifyError, notifyInfo, notifyWarning } = useAudioEditorNotifications();
    const { tracks, newTrack, setSessionAudio, setConvertedAudioList } = useAudioEditor();

    // The load sequence awaits between steps, so it reads the timeline through a ref
    // rather than through a captured render value.
    const tracksRef = useRef(tracks);
    useEffect(() => {
        tracksRef.current = tracks;
    }, [tracks]);

    // One run per opened audio, even though newTrack is registered asynchronously by the mixer.
    const startedForRef = useRef<string | null>(null);

    /**
     * Wait for the mixer to report the loaded voice, which is when its real duration is
     * known. Polling keeps this independent of how the mixer schedules its state syncs.
     */
    const waitForLoadedTrack = useCallback(async (trackId: string): Promise<AudioEditorTrackType | null> => {
        for (let attempt = 0; attempt < 40; attempt += 1) {
            const loaded = tracksRef.current.find((track) => track.id === trackId);
            if (loaded && (loaded.cueOut ?? 0) > 0) return loaded;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return null;
    }, []);

    useEffect(() => {
        if (!audioAIId || !newTrack) return;
        if (startedForRef.current === audioAIId) return;
        startedForRef.current = audioAIId;

        // Deliberately not cancellable. The effect re-runs whenever `newTrack` changes
        // identity, which happens on every `tracks` update — so loading a clip re-triggers
        // this effect. A cleanup that aborted the run would stop the loading loop halfway
        // and, because of the guard above, never resume: clips would go missing silently.
        // Loading is also not safely resumable, since clips already sit in the playlist.
        // The guard makes a second run a no-op, which is all the protection needed.
        const bootstrap = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;

            let payload;
            try {
                payload = await getAudioComposition({ token, audioAIId });
            } catch (error) {
                notifyError(error, t("audioCompositionLoadFailed"));
                return;
            }
            if (!payload?.voiceAudio) return;

            const { voiceAudio, composition, inherited, sourceLanguage } = payload;
            const voiceUrl = voiceAudio.audioUrl;
            if (!voiceUrl) return;

            setSessionAudio(toEditorSession(payload));

            // Fill the media strip so every clip can be dropped back onto the timeline.
            // For a reopened mix the voice lives in the composition, not in `audioUrl`,
            // which is the flattened render.
            setConvertedAudioList(
                buildImportedList(
                    composition,
                    composition && !inherited ? null : { id: voiceAudio.id, name: voiceAudio.name, url: voiceUrl },
                ),
            );

            // Reopening a mix: restore the timeline exactly as it was saved, voice clips
            // included. `audioUrl` is the flattened render, so it must NOT be laid down as
            // a track — that would stack the sound design on top of itself.
            if (composition && !inherited) {
                for (const clip of composition.tracks) {
                    await newTrack(clipToTrack(clip));
                }
                return;
            }

            // Otherwise the audio is a plain capsule: its own voice goes in as a single,
            // uncut clip. Cut points belong to the signal they were made on and cannot be
            // transposed onto another language.
            const createdVoiceId = await newTrack({
                id: uuidv4(),
                name: voiceAudio.name,
                url: voiceUrl,
                description: voiceAudio.text ?? "",
                role: "voice",
                sourceKind: "audioAI",
                sourceId: voiceAudio.id,
                sourceText: voiceAudio.text ?? null,
                // Who speaks it. Without this the audio being edited contributes a block
                // with no voice, and the content loses its main voice at translation.
                sourceVoice: {
                    voiceAIId: voiceAudio.voiceAIId,
                    voiceCloneId: voiceAudio.voiceCloneId,
                    voicePromptId: voiceAudio.voicePromptId,
                    voiceLabel: voiceAudio.voiceLabel,
                    isDialog: voiceAudio.isDialog,
                },
                // Its replies, when it is a dialogue: each keeps its own voice.
                sourceDialogLines: (voiceAudio.dialogLines || []).map((line) => ({
                    order: line.order,
                    text: line.text,
                    voiceAIId: line.voiceAIId,
                    voiceCloneId: line.voiceCloneId,
                    voicePromptId: line.voicePromptId,
                    voiceName: line.voiceName,
                })),
                // Keep the onset of the source voice: the sound design was timed against
                // a narration starting there, and moving it to zero shifts everything.
                startTime: composition ? voiceStartOf(composition.tracks) : 0,
            });

            if (!composition || composition.tracks.length === 0) return;

            const loadedVoice = createdVoiceId ? await waitForLoadedTrack(createdVoiceId) : null;
            if (!loadedVoice) {
                notifyWarning(t("audioCompositionVoiceTooSlow"));
                return;
            }

            // Positions scale with the duration ratio: a sound heard 20% into the source
            // narration lands 20% into this one. Lengths are left alone, since an audio
            // file cannot be stretched without time-stretching it.
            // Both sides measure the speech itself, never its position on the timeline.
            const voiceDuration = (loadedVoice.cueOut ?? 0) - (loadedVoice.cueIn ?? 0);
            // Without an anchor on the source side there is nothing to scale from, so the
            // clips keep the positions they were saved with.
            const sourceVoiceDuration = composition.voiceDuration ?? 0;
            const ratio = sourceVoiceDuration > 0 ? voiceDuration / sourceVoiceDuration : 1;
            const pivot = voiceStartOf(composition.tracks);

            for (const clip of rescaleCompositionClips(composition.tracks, ratio, pivot)) {
                await newTrack(clipToTrack(clip));
            }

            notifyInfo(t("audioCompositionInherited", { language: sourceLanguage || "" }));
            if (composition.hasVoiceCuts) {
                notifyWarning(t("audioCompositionVoiceWasCut"));
            }
        };

        bootstrap();
    }, [
        audioAIId,
        newTrack,
        setSessionAudio,
        setConvertedAudioList,
        waitForLoadedTrack,
        notifyError,
        notifyInfo,
        notifyWarning,
        t,
    ]);

    return null;
};

export default AudioEditorBootstrap;
